/**
 * Dashboard Boot Hook - LCP Optimization
 * 
 * Single round-trip fetch for critical above-the-fold data:
 * - Funnel metrics
 * - Recommended Creators
 * - Live Assets
 * 
 * Purpose: Reduce mobile LCP from 3.3-4.0s → <2s
 * Strategy: Replace 3 sequential useSimpleSnapshot calls with 1 RPC call
 * Expected Impact: Saves ~1,500ms on Slow 4G networks
 */

import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useSnapshotScope } from '../contexts/SnapshotScope';
import { useUser } from '../contexts/UserContext';
import { logger } from '../utils/logger';
import type { DashboardBootParams, DashboardBootResponse } from '../types/supabase-rpc';

export interface DashboardBootData {
  funnel: {
    newCreators: number;
    inSequence: number;
    inNegotiation: number;
    activeCreators: number;
  };
  creators: any[];
  assets: any[];
  weekKey: string;
  workspaceId: string;
  timestamp: string;
  cacheKey: string;
}

export interface UseDashboardBootReturn {
  data: DashboardBootData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch all critical dashboard data in a single round-trip
 * Replaces sequential calls to useSimpleSnapshot for funnel, creators, and assets
 */
export function useDashboardBoot(): UseDashboardBootReturn {
  const { workspaceId, periodKey } = useSnapshotScope();
  const { role } = useUser();
  const [data, setData] = useState<DashboardBootData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const loadSeq = useRef(0);
  const pendingRequest = useRef<Promise<any> | null>(null);
  const pendingRequestKey = useRef<string | null>(null);

  // Cleanup: Clear pending request on unmount
  useEffect(() => {
    return () => {
      pendingRequest.current = null;
      pendingRequestKey.current = null;
    };
  }, []);

  const fetchBootData = async () => {
    if (!workspaceId || !periodKey) {
      if (import.meta.env.DEV) {
        logger.debug('Waiting for workspace/week to resolve', undefined, 'useDashboardBoot');
      }
      setLoading(false);
      return;
    }

    const seq = ++loadSeq.current;
    
    try {
      setLoading(true);
      
      // Performance mark
      performance.mark('dashboard_boot_start');
      
      if (import.meta.env.DEV) {
        logger.debug('Fetching boot data', { workspaceId, periodKey, role }, 'useDashboardBoot');
      }
      
      // Check for pending request to prevent duplicates
      const requestKey = `${workspaceId}:${periodKey}`;
      
      if (!pendingRequest.current || pendingRequestKey.current !== requestKey) {
        // Single RPC call that returns all critical data
        const params: DashboardBootParams = {
          p_workspace_id: workspaceId,
          p_week_key: periodKey,
          p_user_role: role as string | null
        };
        
        // Type assertion needed because Supabase RPC types are not fully generated
        // The RPC function signature is not in the generated types, so we cast to unknown first
        pendingRequest.current = (supabase.rpc('dashboard_boot', params as unknown as never) as unknown as Promise<DashboardBootResponse>).then(
          (response) => ({
            data: response.data as DashboardBootData | null,
            error: response.error
          })
        );
        pendingRequestKey.current = requestKey;
      }
      
      let bootData, rpcError;
      try {
        const result = await pendingRequest.current;
        bootData = result.data;
        rpcError = result.error;
      } catch (err) {
        rpcError = err;
        setError(err as Error);
      } finally {
        if (pendingRequestKey.current === requestKey) {
          pendingRequest.current = null; // Always clear matching request
          pendingRequestKey.current = null;
        }
      }
      
      // Stale check
      if (loadSeq.current !== seq) {
        if (import.meta.env.DEV) {
          logger.debug('Stale response ignored', { requestKey }, 'useDashboardBoot');
        }
        return;
      }
      
      // Handle RPC errors gracefully - if RPC doesn't exist (404), fall back to sequential loading
      if (rpcError) {
        // Check if it's a "function doesn't exist" error (PGRST42883 or 404)
        const isRpcNotFound = rpcError.code === 'PGRST42883' || 
                              rpcError.message?.includes('42883') ||
                              rpcError.message?.includes('does not exist') ||
                              (rpcError && typeof rpcError === 'object' && 'status' in rpcError && (rpcError as { status?: number }).status === 404);
        
        if (isRpcNotFound) {
          // RPC doesn't exist - fall back to sequential loading
          if (import.meta.env.DEV) {
            logger.warn('dashboard_boot RPC not found, falling back to sequential loading', { error: rpcError }, 'useDashboardBoot');
          }
          
          // Import here to avoid circular dependencies
          const { getSnapshot } = await import('../supabase/snapshotService');
          
          // Fetch in parallel (better than sequential but still 3 requests)
          const [funnelResult, creatorsResult, assetsResult] = await Promise.all([
            getSnapshot(periodKey, 'funnel', workspaceId),
            getSnapshot(periodKey, 'recommendedCreators', workspaceId),
            getSnapshot(periodKey, 'liveAssets', workspaceId)
          ]);
          
          const funnelData = funnelResult?.data ?? null;
          const creatorsData = creatorsResult?.data ?? null;
          const assetsData = assetsResult?.data ?? null;
          
          // Normalize funnel data format (array to object if needed)
          let normalizedFunnel = funnelData;
          if (Array.isArray(funnelData) && funnelData.length > 0) {
            // Convert array format to object format
            normalizedFunnel = {
              newCreators: (funnelData as Array<{ key: string; value: number }>).find((f) => f.key === 'newCreators')?.value || 0,
              inSequence: (funnelData as Array<{ key: string; value: number }>).find((f) => f.key === 'inSequence')?.value || 0,
              inNegotiation: (funnelData as Array<{ key: string; value: number }>).find((f) => f.key === 'inNegotiation')?.value || 0,
              activeCreators: (funnelData as Array<{ key: string; value: number }>).find((f) => f.key === 'activeCreators')?.value || 0
            };
          } else if (!funnelData || typeof funnelData !== 'object') {
            normalizedFunnel = {
              newCreators: 0,
              inSequence: 0,
              inNegotiation: 0,
              activeCreators: 0
            };
          }
          
          setData({
            funnel: normalizedFunnel as { newCreators: number; inSequence: number; inNegotiation: number; activeCreators: number },
            creators: (Array.isArray(creatorsData) ? creatorsData : []) || [],
            assets: (Array.isArray(assetsData) ? assetsData : []) || [],
            weekKey: periodKey,
            workspaceId: workspaceId,
            timestamp: new Date().toISOString(),
            cacheKey: `${workspaceId}:${periodKey}`
          });
          setError(null);
          
          performance.mark('dashboard_boot_end');
          performance.measure('dashboard_boot_latency', 'dashboard_boot_start', 'dashboard_boot_end');
          
          return; // Successfully loaded via fallback
        }
        
        // Other RPC errors - throw to be caught by outer catch
        throw rpcError;
      }
      
      // Performance measurement
      performance.mark('dashboard_boot_end');
      performance.measure('dashboard_boot_latency', 'dashboard_boot_start', 'dashboard_boot_end');
      
      const measures = performance.getEntriesByName('dashboard_boot_latency');
      if (measures.length > 0 && import.meta.env.DEV) {
        const latency = measures[measures.length - 1].duration;
        logger.debug('Boot data loaded', {
          latency: `${latency.toFixed(0)}ms`,
          funnel: bootData?.funnel ? 'loaded' : 'empty',
          creators: Array.isArray(bootData?.creators) ? `${bootData.creators.length} items` : 'empty',
          assets: Array.isArray(bootData?.assets) ? `${bootData.assets.length} items` : 'empty'
        }, 'useDashboardBoot');
      }
      
      const normalizedBootData = (bootData ?? null) as DashboardBootData | null;
      setData(normalizedBootData);
      setError(null);
      
    } catch (err) {
      logger.error('Failed to fetch boot data', { error: err }, 'useDashboardBoot');
      
      if (loadSeq.current === seq) {
        setError(err as Error);
        
        // Fallback to empty defaults on error
        setData({
          funnel: {
            newCreators: 0,
            inSequence: 0,
            inNegotiation: 0,
            activeCreators: 0
          },
          creators: [],
          assets: [],
          weekKey: periodKey,
          workspaceId: workspaceId,
          timestamp: new Date().toISOString(),
          cacheKey: `${workspaceId}:${periodKey}`
        });
      }
    } finally {
      if (loadSeq.current === seq) {
        setLoading(false);
      }
    }
  };

  // Load data when workspace/week are ready
  useEffect(() => {
    if (!workspaceId || !periodKey) {
      setLoading(false);
      return;
    }

    fetchBootData();
    
    // Cleanup pending request on unmount
    return () => {
      pendingRequest.current = null;
      pendingRequestKey.current = null;
    };
  }, [workspaceId, periodKey, role]);

  return {
    data,
    loading,
    error,
    refetch: fetchBootData
  };
}
